/**
 * ResearchDataExplorerPageV2 — redesigned Research Data Explorer.
 *
 * Layout: PageHeader + three FormSections.
 *
 *   01 Filters — date range, completion status, platform, free-text
 *      filters (session type / subscription type / condition), with
 *      Search (coral) + Export CSV (ghost) actions.
 *   02 Results — LibTable with dynamic columns inferred from the
 *      first row (BigQuery query result shape varies). Custom pager
 *      strip below.
 *   03 Past exports — LibTable of generation jobs with status pill,
 *      counts, timestamps, and per-row Download / Chain of custody
 *      actions. 5s polling for pending/processing jobs (parity v1).
 *
 * Chain of custody dialog ported to the v2 Modal primitive — same
 * plain-text format as v1, with Copy / Download buttons.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import {
  queryResearchData,
  exportResearchData,
  checkExportJobStatus,
  fetchExportJobs,
  downloadResearchExport,
  fetchChainOfCustody,
} from '../../../../actions/researchDataActions';
import {
  PageHeader,
  Button,
  LibTable,
  Modal,
  StatusPill,
} from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Select,
  DatePicker,
} from '../../../redesign/edit';

const SearchIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const DownloadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const STATUS_TONE = {
  pending:    'amber',
  processing: 'blue',
  completed:  'green',
  failed:     'coral',
  expired:    'gray',
};

function formatTimestamp(iso) {
  if (!iso) return 'N/A';
  try {
    return format(new Date(iso), 'MMM dd, yyyy HH:mm');
  } catch {
    return iso;
  }
}

const ROWS_PER_PAGE_OPTIONS = [100, 250, 500, 1000];

export default function ResearchDataExplorerPageV2() {
  const dispatch = useDispatch();

  // Filters
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [completionStatus, setCompletionStatus] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [platform, setPlatform] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('');
  const [condition, setCondition] = useState('');

  // Data
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const searchIdRef = useRef(0);

  // Export jobs
  const [exportJobs, setExportJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const pollingIntervalsRef = useRef({});
  const skipPaginationEffect = useRef(false);

  // Download state
  const [downloadingJobIds, setDownloadingJobIds] = useState(new Set());

  // Chain of custody
  const [cocOpen, setCocOpen] = useState(false);
  const [cocData, setCocData] = useState(null);
  const [isLoadingCoc, setIsLoadingCoc] = useState(false);

  const isValidDate = (d) => d instanceof Date && !isNaN(d);

  const buildFilters = () => {
    const f = {};
    if (dateFrom && isValidDate(dateFrom)) f.date_from = format(dateFrom, 'yyyy-MM-dd');
    if (dateTo && isValidDate(dateTo)) f.date_to = format(dateTo, 'yyyy-MM-dd');
    if (completionStatus) f.completion_status = completionStatus;
    if (sessionType) f.session_type = sessionType;
    if (platform) f.platform = platform;
    if (subscriptionType) f.subscription_type = subscriptionType;
    if (condition) f.condition = condition;
    return f;
  };

  /* ─── COC text formatter (verbatim from v1) ─────────────────── */
  const formatCocAsText = useCallback((data) => {
    if (!data) return '';
    const lines = [
      '============================================================',
      'CHAIN OF CUSTODY DOCUMENTATION',
      'Master Data Export for Research Publications',
      '============================================================',
      '',
      `Generated:        ${data.generated_at || 'N/A'}`,
      `Export Job ID:     ${data.job_id || 'N/A'}`,
      `Export Type:       ${data.job_type || 'N/A'}`,
      `Code Version:      ${data.code_version || 'N/A'} (${data.code_version_full || 'N/A'})`,
      '',
      'DATE RANGE',
      '----------',
      `From:  ${data.date_range?.from || 'N/A'}`,
      `To:    ${data.date_range?.to || 'N/A'}`,
      '',
      'DATA SUMMARY',
      '------------',
      `Total Rows:      ${data.data_summary?.total_rows?.toLocaleString() || 'N/A'}`,
      `Unique Users:    ${data.data_summary?.unique_users?.toLocaleString() || 'N/A'}`,
      '',
      'FILE INTEGRITY',
      '--------------',
      `File Name:       ${data.file_integrity?.file_name || 'N/A'}`,
      `CSV SHA-256:     ${data.file_integrity?.csv_sha256 || 'N/A'}`,
      `GCS Key:         ${data.file_integrity?.gcs_key || 'N/A'}`,
      `BigQuery Loaded: ${data.file_integrity?.bigquery_loaded ? 'Yes' : 'No'}`,
      '',
      'ANONYMIZATION',
      '-------------',
      `User IDs:        ${data.anonymization?.user_ids || 'N/A'}`,
      `PII Fields:      ${data.anonymization?.pii_fields || 'N/A'}`,
      '',
      'COLUMNS EXPORTED',
      '----------------',
      data.columns?.join(', ') || 'N/A',
    ];
    if (data.known_caveats?.length > 0) {
      lines.push('', 'KNOWN CAVEATS', '-------------');
      data.known_caveats.forEach((c, i) => lines.push(`${i + 1}. ${c}`));
    }
    lines.push('', '============================================================');
    return lines.join('\n');
  }, []);

  /* ─── Search ────────────────────────────────────────────────── */
  const handleSearch = async (overridePage) => {
    const searchPage = overridePage !== undefined ? overridePage : page;
    const currentSearchId = ++searchIdRef.current;
    setIsLoadingData(true);
    try {
      const result = await dispatch(
        queryResearchData(buildFilters(), searchPage + 1, rowsPerPage),
      );
      if (currentSearchId !== searchIdRef.current) return;
      if (result) {
        setRows(result.data || []);
        setTotalCount(result.total_count || 0);
      }
    } catch (e) {
      if (currentSearchId !== searchIdRef.current) return;
      console.error('Failed to query research data:', e);
      Swal.fire({ icon: 'error', title: 'Failed to query data' });
    } finally {
      if (currentSearchId === searchIdRef.current) setIsLoadingData(false);
    }
  };

  // Re-fetch when pagination changes after initial search.
  useEffect(() => {
    if (skipPaginationEffect.current) {
      skipPaginationEffect.current = false;
      return;
    }
    if (rows.length > 0 || page > 0) {
      handleSearch(page);
    }
    // page + rowsPerPage are the inputs; other deps captured at call time.
  }, [page, rowsPerPage]);

  /* ─── Export jobs ────────────────────────────────────────────── */
  const startPolling = (jobId) => {
    if (pollingIntervalsRef.current[jobId]) return;
    const interval = setInterval(async () => {
      try {
        const job = await dispatch(checkExportJobStatus(jobId));
        if (job) {
          setExportJobs((prev) =>
            prev.map((j) => (j.job_id === jobId ? job : j)),
          );
          if (['completed', 'failed', 'expired'].includes(job.status)) {
            clearInterval(pollingIntervalsRef.current[jobId]);
            delete pollingIntervalsRef.current[jobId];
          }
        }
      } catch (e) {
        console.error(`Failed status check for ${jobId}:`, e);
        clearInterval(pollingIntervalsRef.current[jobId]);
        delete pollingIntervalsRef.current[jobId];
      }
    }, 5000);
    pollingIntervalsRef.current[jobId] = interval;
  };

  const loadExportJobs = async (preserveOptimistic = false) => {
    setIsLoadingJobs(true);
    try {
      const jobs = await dispatch(fetchExportJobs());
      if (Array.isArray(jobs)) {
        setExportJobs((prev) => {
          if (preserveOptimistic) {
            const ids = new Set(jobs.map((j) => j.job_id));
            const optimistic = prev.filter((j) => !ids.has(j.job_id));
            return [...optimistic, ...jobs];
          }
          return jobs;
        });
        jobs.forEach((j) => {
          if (
            (j.status === 'pending' || j.status === 'processing') &&
            !pollingIntervalsRef.current[j.job_id]
          ) {
            startPolling(j.job_id);
          }
        });
      }
    } catch (e) {
      console.error('Failed to load export jobs:', e);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  useEffect(() => {
    loadExportJobs();
    const intervals = pollingIntervalsRef.current;
    return () => {
      Object.values(intervals).forEach((id) => clearInterval(id));
    };
  }, []);

  const handleExport = async () => {
    try {
      const job = await dispatch(exportResearchData(buildFilters()));
      if (job?.job_id) {
        setExportJobs((prev) => [job, ...prev]);
        startPolling(job.job_id);
        Swal.fire({
          icon: 'success',
          title: 'Export started',
          timer: 1500,
          showConfirmButton: false,
        });
        loadExportJobs(true);
      }
    } catch (e) {
      console.error('Failed to start export:', e);
      Swal.fire({ icon: 'error', title: 'Failed to start export' });
    }
  };

  const handleDownload = async (jobId) => {
    setDownloadingJobIds((prev) => new Set(prev).add(jobId));
    try {
      await dispatch(downloadResearchExport(jobId));
    } catch (e) {
      console.error('Download failed:', e);
      Swal.fire({ icon: 'error', title: 'Download failed' });
    } finally {
      setDownloadingJobIds((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleViewCoc = async (jobId) => {
    setIsLoadingCoc(true);
    setCocOpen(true);
    setCocData(null);
    try {
      const data = await dispatch(fetchChainOfCustody(jobId));
      setCocData(data);
    } catch (e) {
      console.error('Failed to fetch chain of custody:', e);
      Swal.fire({ icon: 'error', title: 'Failed to load chain of custody' });
      setCocOpen(false);
    } finally {
      setIsLoadingCoc(false);
    }
  };

  const handleCopyCoc = () => {
    const text = formatCocAsText(cocData);
    navigator.clipboard.writeText(text).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Copied to clipboard',
        timer: 1200,
        showConfirmButton: false,
      });
    });
  };

  const handleDownloadCoc = () => {
    const text = formatCocAsText(cocData);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chain_of_custody_job_${cocData?.job_id || 'unknown'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];
  const totalPages = totalCount > 0 ? Math.max(1, Math.ceil(totalCount / rowsPerPage)) : 1;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Reports"
        title="Research Data Explorer"
      />

      <div style={{ padding: '0 28px 60px' }}>
        {/* 01 Filters */}
        <FormSection title="Filters" num="01">
          <FieldRow cols={3}>
            <FieldBlock label="Date from" noMargin>
              <DatePicker value={dateFrom} onChange={setDateFrom} />
            </FieldBlock>
            <FieldBlock label="Date to" noMargin>
              <DatePicker value={dateTo} onChange={setDateTo} />
            </FieldBlock>
            <FieldBlock label="Completion" htmlFor="f-rd-completion" noMargin>
              <Select
                id="f-rd-completion"
                value={completionStatus}
                onChange={(e) => setCompletionStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="abandoned">Abandoned</option>
              </Select>
            </FieldBlock>
          </FieldRow>
          <FieldRow cols={3}>
            <FieldBlock label="Platform" htmlFor="f-rd-platform" noMargin>
              <Select
                id="f-rd-platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
              >
                <option value="">All</option>
                <option value="iOS">iOS</option>
                <option value="Android">Android</option>
                <option value="Web">Web</option>
              </Select>
            </FieldBlock>
            <FieldBlock label="Session type" htmlFor="f-rd-session" noMargin>
              <TextInput
                id="f-rd-session"
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value)}
              />
            </FieldBlock>
            <FieldBlock label="Subscription type" htmlFor="f-rd-subscription" noMargin>
              <TextInput
                id="f-rd-subscription"
                value={subscriptionType}
                onChange={(e) => setSubscriptionType(e.target.value)}
              />
            </FieldBlock>
          </FieldRow>
          <FieldBlock label="Condition" htmlFor="f-rd-condition" noMargin>
            <TextInput
              id="f-rd-condition"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
            />
          </FieldBlock>
          <div className="flex items-center" style={{ gap: 8, marginTop: 14 }}>
            <Button
              variant="coral"
              leadingIcon={SearchIconSvg}
              onClick={() => {
                if (page !== 0) {
                  skipPaginationEffect.current = true;
                  setPage(0);
                }
                handleSearch(0);
              }}
            >
              Search
            </Button>
            <Button
              variant="ghost"
              leadingIcon={DownloadIcon}
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </div>
        </FormSection>

        {/* 02 Results */}
        <FormSection
          title="Results"
          num="02"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              {totalCount > 0 ? `${totalCount.toLocaleString()} rows` : '—'}
            </span>
          }
        >
          {isLoadingData ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              Querying BigQuery…
            </div>
          ) : rows.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              No data yet. Apply filters and click Search.
            </div>
          ) : (
            <>
              <div
                className="border border-sand"
                style={{ borderRadius: 12, overflow: 'hidden', maxHeight: 600, overflowY: 'auto' }}
              >
                <LibTable fixed style={{ background: '#FAF7F1' }}>
                  <LibTable.Head>
                    <tr>
                      {columns.map((c) => (
                        <LibTable.Th key={c} style={{ whiteSpace: 'nowrap' }}>
                          {c}
                        </LibTable.Th>
                      ))}
                    </tr>
                  </LibTable.Head>
                  <tbody>
                    {rows.map((row, idx) => (
                      <LibTable.Row key={idx}>
                        {columns.map((c) => (
                          <LibTable.Td
                            key={c}
                            style={{
                              whiteSpace: 'nowrap',
                              maxWidth: 320,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: 12,
                            }}
                            title={row[c] != null ? String(row[c]) : ''}
                          >
                            <span className={typeof row[c] === 'number' ? 'font-mono text-navy' : 'text-navy'}>
                              {row[c] != null ? String(row[c]) : ''}
                            </span>
                          </LibTable.Td>
                        ))}
                      </LibTable.Row>
                    ))}
                  </tbody>
                </LibTable>
              </div>
              <BigPager
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                rowsPerPage={rowsPerPage}
                onPrev={() => page > 0 && setPage(page - 1)}
                onNext={() => page < totalPages - 1 && setPage(page + 1)}
                onPerPage={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </FormSection>

        {/* 03 Past exports */}
        <FormSection
          title="Past exports"
          num="03"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              {exportJobs.length.toLocaleString()} total
            </span>
          }
        >
          {isLoadingJobs && exportJobs.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              Loading export jobs…
            </div>
          ) : exportJobs.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              No exports yet — use Export CSV above to start one.
            </div>
          ) : (
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                {/* job id */}
                <col style={{ width: 220 }} />
                {/* status */}
                <col style={{ width: 120 }} />
                {/* Rows + Users — comma-separated counts that can
                 *  hit 7-figure territory ("1,234,567"). Previously
                 *  90/110 wrapped on big exports; bumped so the
                 *  longest realistic value fits one-line. */}
                {/* rows */}
                <col style={{ width: 130 }} />
                {/* users */}
                <col style={{ width: 130 }} />
                {/* created */}
                <col style={{ width: 160 }} />
                {/* completed */}
                <col style={{ width: 160 }} />
                {/* actions */}
                <col />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th>Job ID</LibTable.Th>
                  <LibTable.CenterTh>Status</LibTable.CenterTh>
                  <LibTable.NumTh>Rows</LibTable.NumTh>
                  <LibTable.NumTh>Users</LibTable.NumTh>
                  <LibTable.Th>Created</LibTable.Th>
                  <LibTable.Th>Completed</LibTable.Th>
                  <LibTable.Th>Actions</LibTable.Th>
                </tr>
              </LibTable.Head>
              <tbody>
                {exportJobs.map((j) => {
                  const tone = STATUS_TONE[j.status] || 'gray';
                  const isDownloading = downloadingJobIds.has(j.job_id);
                  return (
                    <LibTable.Row key={j.job_id}>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted truncate block" style={{ fontSize: 11.5 }} title={j.job_id}>
                          {j.job_id}
                        </span>
                      </LibTable.Td>
                      <LibTable.CenterTd>
                        <StatusPill tone={tone}>{j.status}</StatusPill>
                      </LibTable.CenterTd>
                      <LibTable.NumTd muted={j.row_count == null} style={{ whiteSpace: 'nowrap' }}>
                        {j.row_count != null ? j.row_count.toLocaleString() : '—'}
                      </LibTable.NumTd>
                      <LibTable.NumTd muted={j.unique_users == null} style={{ whiteSpace: 'nowrap' }}>
                        {j.unique_users != null ? j.unique_users.toLocaleString() : '—'}
                      </LibTable.NumTd>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {formatTimestamp(j.created_at)}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {j.completed_at ? formatTimestamp(j.completed_at) : '—'}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        {j.status === 'completed' ? (
                          <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                            <Button
                              variant="ghost"
                              leadingIcon={DownloadIcon}
                              onClick={() => handleDownload(j.job_id)}
                              disabled={isDownloading}
                            >
                              {isDownloading ? 'Preparing…' : 'Download'}
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => handleViewCoc(j.job_id)}
                            >
                              Chain of Custody
                            </Button>
                          </div>
                        ) : j.status === 'failed' ? (
                          <span className="text-coral" style={{ fontSize: 12 }}>
                            {j.error_message || 'Export failed'}
                          </span>
                        ) : (
                          <span className="text-text-muted italic" style={{ fontSize: 12 }}>
                            In progress…
                          </span>
                        )}
                      </LibTable.Td>
                    </LibTable.Row>
                  );
                })}
              </tbody>
            </LibTable>
          )}
        </FormSection>
      </div>

      {/* Chain of custody modal */}
      <Modal
        open={cocOpen}
        onClose={() => setCocOpen(false)}
        eyebrow="Research"
        title="Chain of custody"
        maxWidth={720}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCocOpen(false)}>
              Close
            </Button>
            <Button variant="ghost" onClick={handleDownloadCoc} disabled={!cocData}>
              Download .txt
            </Button>
            <Button variant="coral" onClick={handleCopyCoc} disabled={!cocData}>
              Copy
            </Button>
          </>
        }
      >
        {isLoadingCoc ? (
          <div className="text-text-muted italic" style={{ fontSize: 13 }}>
            Loading…
          </div>
        ) : cocData ? (
          <pre
            className="font-mono text-navy"
            style={{
              background: '#FAF4EA',
              border: '1px solid #E8E0CF',
              borderRadius: 8,
              padding: '12px 14px',
              fontSize: 11,
              lineHeight: 1.5,
              maxHeight: '60vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
            }}
          >
            {formatCocAsText(cocData)}
          </pre>
        ) : null}
      </Modal>
    </div>
  );
}

/* ─── Pager for the data table — different from the shared Pager
 *  because this page is 0-indexed and tracks totalPages directly. */
function BigPager({ page, totalPages, totalCount, rowsPerPage, onPrev, onNext, onPerPage }) {
  const from = totalCount === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, totalCount);
  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: '12px 14px',
        marginTop: 8,
        borderRadius: 8,
        background: '#FAF7F1',
        border: '1px solid #E8E0CF',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div className="flex items-center" style={{ gap: 8 }}>
        <span
          className="font-condensed font-bold uppercase text-text-muted"
          style={{ fontSize: 10.5, letterSpacing: '0.08em' }}
        >
          Page size
        </span>
        <select
          value={rowsPerPage}
          onChange={onPerPage}
          style={{
            fontFamily: 'inherit',
            fontSize: 12.5,
            padding: '4px 8px',
            border: '1px solid #E8E0CF',
            borderRadius: 6,
            background: '#FAF4EA',
            color: '#2C3E50',
          }}
        >
          {ROWS_PER_PAGE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center" style={{ gap: 14 }}>
        <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
          {from.toLocaleString()}–{to.toLocaleString()} of {totalCount.toLocaleString()}
        </span>
        <button
          type="button"
          onClick={onPrev}
          disabled={page <= 0}
          className="font-condensed font-bold uppercase"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.08em',
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #E8E0CF',
            background: page <= 0 ? '#FAF4EA' : '#FAF7F1',
            color: page <= 0 ? '#9BA4AD' : '#2C3E50',
            cursor: page <= 0 ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ← Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= totalPages - 1}
          className="font-condensed font-bold uppercase"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.08em',
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #E8E0CF',
            background: page >= totalPages - 1 ? '#FAF4EA' : '#FAF7F1',
            color: page >= totalPages - 1 ? '#9BA4AD' : '#2C3E50',
            cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
